// Custom corporate gifts with a logo. Targets (SERPs checked 2026-11-03):
// custom corporate gifts 720 ($13.32), corporate gifts with logo 480 ($14.91),
// personalized corporate gifts 480 (KD 0), branded gifts 320. The head terms
// "corporate gifts" and "employee appreciation gifts" return catalogs and
// gift-idea lists, so they are secondary here. Corporate buyers are the
// best-paying engraving clients in the inbox (about $3.1K a job).
const {
  quoted,
  setup,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  ownItemsAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'corporateGifts',
  slug: 'corporate-gifts',
  path: '/services/laser-engraving/corporate-gifts/',
  noun: 'gift',
  crumb: 'Corporate gifts',
  title: 'Custom Corporate Gifts with Your Logo | PrintAWorld',
  description:
    'Custom corporate gifts with your logo, engraved in NYC: personalized client and employee gifts in steel, wood, leather and metal, with a name on each.',
  eyebrow: 'Corporate gifts / laser engraved in New York',
  heading:
    'Custom corporate gifts with your logo, engraved, with a name on every piece.',
  answer: `PrintAWorld makes custom corporate gifts with your logo in New York: personalized client gifts, employee gifts and branded gifts, laser engraved, and a different name on each piece if you want one. We engrave tumblers, cutting boards, leather notebooks, pens, card holders, ornaments and plaques, from ten pieces to a few hundred. Engraving is a ${setup} setup plus ${small} for small items; send your own items, or we source them (${quoted.toLowerCase()}).`,
  cta: 'Get a gift quote',
  heroImage: 'giftBox',
  ideaImages: ['employeeNames', 'clientBoard', 'deskSet'],
  images: {
    giftBox: {
      file: 'gift-box-set',
      alt: 'Black gift box with an engraved navy tumbler, walnut coasters and a leather notebook, all with the same logo',
      caption: 'A client gift box, one logo throughout',
    },
    employeeNames: {
      file: 'employee-names',
      alt: 'Row of black tumblers engraved with the same company mark and a different first name on each',
      caption: 'A name on every employee gift',
    },
    clientBoard: {
      file: 'client-board-set',
      alt: 'Walnut cutting board engraved with a company logo and Thank You 2026, tied with kraft ribbon',
      caption: 'A thank-you board for clients',
    },
    deskSet: {
      file: 'desk-set',
      alt: 'Engraved brass pen, black metal card holder and leather key fob on a slate desk',
      caption: 'Desk gifts in metal and leather',
    },
  },
  intro: {
    eyebrow: 'Gifts people keep',
    title: 'Corporate gifts with your logo, engraved instead of printed.',
    paragraphs: [
      'Printed swag wears off. A laser engraves your logo into the steel, wood or leather, so the gift still looks right years later, and each piece can carry a name.',
      'Send your logo, the gift you have in mind and the count. We suggest items that engrave well, engrave one sample for your approval, then the run.',
    ],
  },
  ideas: {
    eyebrow: 'Corporate gift ideas',
    title: 'Engraved gifts for clients, teams and new hires.',
  },
  uses: {
    eyebrow: 'Who they are for',
    title: 'Personalized corporate gifts for clients, employees and new hires.',
    items: [
      {
        label: 'Clients',
        title: 'Client gifts for the holidays',
        body: 'A logo tumbler, a board or a notebook in a gift box, sent with the year-end card or handed over at a meeting.',
      },
      {
        label: 'Employees',
        title: 'Employee appreciation gifts',
        body: 'The company logo plus each person’s name, from a spreadsheet you send. Employee Appreciation Day is the first Friday in March.',
      },
      {
        label: 'New hires',
        title: 'Onboarding kits',
        body: 'A bottle, a notebook and a pen with the logo, ready for every first day. Reorder the same design when you hire again.',
      },
      {
        label: 'Milestones',
        title: 'Work anniversaries',
        body: 'Five, ten and twenty years, engraved with the name and the date. See our recognition awards for the bigger moments.',
      },
      {
        label: 'Events',
        title: 'Offsites and conferences',
        body: 'Gifts for every attendee, or names engraved live at the event with our on-site engraving station.',
      },
    ],
  },
  materials: {
    link: 'What we engrave',
    eyebrow: 'What we engrave',
    title: 'Drinkware, wood, leather and metal gifts.',
    items: [
      {
        title: 'Tumblers and bottles',
        body: 'Powder-coated tumblers and bottles engrave through the coating to bright steel. YETI, Stanley or unbranded.',
      },
      {
        title: 'Wood',
        body: 'Cutting boards, coasters, boxes and serving boards in walnut, maple or bamboo, engraved dark brown.',
      },
      {
        title: 'Leather',
        body: 'Notebooks, portfolios, key fobs and patches, engraved a shade darker than the leather.',
      },
      {
        title: 'Metal',
        body: 'Pens, card holders, business cards and tags in stainless, brass and anodized aluminum, on our MOPA laser.',
      },
    ],
  },
  process: {
    title: 'From your logo to a box of finished gifts.',
    note: 'Standard lead time is 2–3 weeks after the items reach us. For holiday gifts, send your request by mid-November.',
    steps: [
      {
        title: 'Send the logo and count',
        body: 'Your logo, the gift you want or a budget per person, the count, and the in-hand date.',
      },
      {
        title: 'Approve a sample',
        body: 'We send a proof, then engrave one piece so you see the real result before the run.',
      },
      {
        title: 'We engrave the run',
        body: 'Every gift is engraved and checked, with names matched to your list.',
      },
      {
        title: 'Pack and deliver',
        body: 'Gift boxes and cards if you want them, shipped to your office or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Corporate gift engraving pricing, per piece.',
    cta: 'Price my gifts',
    rows: [
      { label: 'Engraving, per item', value: small },
      { label: 'The gift items, if we source them', value: quoted },
      { label: 'Gift boxes, cards, packing', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Corporate holiday gifts',
    title: 'Holiday client gifts, planned back from your in-hand date.',
    paragraphs: [
      'Tell us when the gifts must be in hand, and we plan the sample, the run and the packing back from that date.',
      'Have a promo supplier already? They can ship the items straight to us, and we engrave them.',
    ],
    cta: 'Plan a gift order',
  },
  faqTitle: 'Corporate gift questions, answered.',
  faqs: [
    {
      question: 'How much do engraved corporate gifts cost?',
      answer: costAnswer(
        small,
        `The gift items themselves, gift boxes and packing are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'What corporate gifts can you engrave?',
      answer:
        'Tumblers and water bottles, cutting boards and coasters, leather notebooks and portfolios, pens, card holders, metal business cards, ornaments, plaques and awards. Send a photo or a link of anything else and we tell you if it engraves well.',
    },
    {
      question: 'Can each employee gift have a different name?',
      answer:
        'Yes. Send the names in a spreadsheet. We keep the logo the same and engrave one name per piece, then check every piece against your list.',
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('gift'),
    },
    {
      question: 'Can you source the gifts, or do we send them?',
      answer: `Either. We can source tumblers, boards, notebooks and other items (${quoted.toLowerCase()}), or your supplier ships the items to us. ${ownItemsAnswer('items')}`,
    },
    {
      question: 'When should we order corporate holiday gifts?',
      answer: timingAnswer(
        'For gifts in hand before the holidays, send the request by mid-November.'
      ),
    },
    {
      question: 'Can you mail each gift to a different person?',
      answer: shipAnswer,
    },
    {
      question: 'What logo file do you need?',
      answer:
        'A vector file (SVG, AI, EPS or PDF) engraves sharpest. A clean PNG works for most logos; we tell you if it needs redrawing.',
    },
  ],
  quote: {
    title: 'Tell us about your gifts.',
    body: 'Send the logo, the gift you have in mind, how many and when you need them. We’ll reply with suggestions and a written quote.',
    checklist: [
      'Your logo, vector if you have it',
      'The gift, or a budget per person',
      'How many, and a names list if each is personal',
      'Your in-hand date and delivery ZIP',
    ],
  },
  moreCards: ['tumblers', 'cuttingBoards', 'christmasOrnaments'],
  extraRelated: [
    ['Recognition awards', '/services/laser-engraving/recognition-awards/'],
    ['On-site event engraving', '/services/on-site-laser-engraving-nyc/'],
  ],
  schema: {
    name: 'Custom Corporate Gifts',
    alternateName: [
      'Corporate gifts with logo',
      'Personalized corporate gifts',
      'Branded gifts',
      'Employee appreciation gifts',
      'Engraved client gifts',
    ],
    serviceType: 'Laser-engraved corporate gifts',
  },
})
